import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AvatarUploader } from "@/components/profile/AvatarUploader";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { User, Mail, Calendar } from "lucide-react";

interface ProfileFormValues {
  full_name: string;
}

export default function ProfileSettings() {
  const [isLoading, setIsLoading] = useState(false);
  const { profile, updateProfile, user } = useAuth();

  const form = useForm<ProfileFormValues>({
    defaultValues: {
      full_name: profile?.full_name || "",
    },
  });

  // Update form when profile changes
  useEffect(() => {
    if (profile) {
      form.reset({
        full_name: profile.full_name || "",
      });
    }
  }, [profile, form]);

  async function onSubmit(data: ProfileFormValues) {
    setIsLoading(true);
    try {
      await updateProfile(data);
      toast.success("Perfil atualizado com sucesso!");
    } catch (error: any) {
      toast.error(error.message || "Erro ao atualizar perfil");
    } finally {
      setIsLoading(false);
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Configurações de Perfil</h1>
        <p className="text-muted-foreground mt-2">
          Gerencie suas informações pessoais e configurações de conta
        </p>
      </div>

      {/* Profile Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Informações Pessoais
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Avatar Section */}
            <div className="lg:col-span-1">
              <div className="space-y-6">
                <div className="flex flex-col items-center space-y-4">
                  <AvatarUploader profile={profile} />
                  <div className="text-center">
                    <h3 className="font-semibold text-lg">{profile?.full_name || "Nome não informado"}</h3>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Informações da Foto
                  </h4>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    <p>• Formatos aceitos: JPG, PNG, GIF</p>
                    <p>• Tamanho máximo: 2MB</p>
                    <p>• Resolução recomendada: 400x400px</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Section */}
            <div className="lg:col-span-2">
              <div className="space-y-8">
                {/* Account Info Display */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <h3 className="text-lg font-semibold">Informações da Conta</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-muted-foreground">Email</label>
                      <div className="flex items-center gap-2">
                        <Input 
                          value={user?.email || ""} 
                          disabled 
                          className="bg-muted/50"
                        />
                        <Badge variant="secondary" className="shrink-0">Verificado</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        O email não pode ser alterado após o cadastro
                      </p>
                    </div>

                    {profile?.created_at && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-muted-foreground">Membro desde</label>
                        <div className="flex items-center gap-2">
                          <Input 
                            value={formatDate(profile.created_at)} 
                            disabled 
                            className="bg-muted/50"
                          />
                          <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Editable Form */}
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold">Dados Editáveis</h3>
                  
                  <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                      <FormField
                        control={form.control}
                        name="full_name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-base font-medium">Nome Completo</FormLabel>
                            <FormControl>
                              <Input 
                                placeholder="Digite seu nome completo" 
                                {...field} 
                                className="text-base h-11"
                              />
                            </FormControl>
                            <FormMessage />
                            <p className="text-xs text-muted-foreground">
                              Este nome será exibido em seus documentos e relatórios
                            </p>
                          </FormItem>
                        )}
                      />

                      <div className="flex justify-end pt-4">
                        <Button 
                          type="submit" 
                          disabled={isLoading}
                          className="min-w-[140px] h-11"
                        >
                          {isLoading ? "Salvando..." : "Salvar Alterações"}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Account Status Card */}
      <Card>
        <CardHeader>
          <CardTitle>Status da Conta</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <div className="h-3 w-3 bg-green-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium">Conta Ativa</p>
                <p className="text-xs text-muted-foreground">Sua conta está ativa e funcionando</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <div className="h-3 w-3 bg-green-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium">Email Verificado</p>
                <p className="text-xs text-muted-foreground">Seu email foi confirmado</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 border rounded-lg">
              <div className="h-3 w-3 bg-blue-500 rounded-full"></div>
              <div>
                <p className="text-sm font-medium">Plano Gratuito</p>
                <p className="text-xs text-muted-foreground">Recursos básicos disponíveis</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
